'use client';

import { useState, useCallback, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Star, Heart } from 'lucide-react';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";

interface PropertyCardProps {
    property: any;
}

export default function PropertyCard({ property }: PropertyCardProps) {
    const [api, setApi] = useState<any>();
    const [currentSlide, setCurrentSlide] = useState(0);

    // Helper function to extract location string from property
    const getLocationString = (location: any, fallback: string = ''): string => {
        if (typeof location === 'string') {
            return location;
        }
        if (location && typeof location === 'object') {
            return location.address || fallback;
        }
        return fallback;
    };

    useEffect(() => {
        if (!api) {
            return;
        }

        api.on("select", () => {
            setCurrentSlide(api.selectedScrollSnap());
        });
    }, [api]);

    const handleDotClick = useCallback((index: number) => {
        if (api) {
            api.scrollTo(index);
        }
    }, [api]);

    const images = Array.isArray(property?.media?.gallery || property?.images) ? (property?.media?.gallery || property?.images) : [];

    return (
        <div className="group h-full overflow-hidden hover:shadow-lg transition-shadow duration-300">
            <div className="relative">
                <Carousel
                    opts={{
                        align: "start",
                        loop: true,
                    }}
                    className="w-full"
                    setApi={setApi}
                >
                    <CarouselContent>
                        {images.map((image: any, idx: number) => (
                            <CarouselItem key={idx}>
                                <div className="relative w-full h-full aspect-[4/3]">
                                    <Image
                                        src={image?.url || image}
                                        alt={`${property.title || 'Property'} - Image ${idx + 1}`}
                                        fill
                                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 rounded-2xl "
                                        priority={idx === 0}
                                    />
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContent>

                    {images.length > 1 && (
                        <>
                            <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white z-10 opactiy-0 group-hover:opacity-100 transition-opacity" />
                            <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white z-10 opacity-0 group-hover:opacity-100 transition-opacity" />

                            {/* Pagination Dots */}
                            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                                {images.slice(0, 5).map((_: any, idx: number) => (
                                    <div
                                        key={idx}
                                        className={`w-2 h-2 rounded-full transition-all duration-200 cursor-pointer ${currentSlide === idx
                                            ? 'bg-white ring-2 ring-white/80 scale-125'
                                            : 'bg-white/60 hover:bg-white/80'
                                            }`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDotClick(idx);
                                        }}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </Carousel>

                {property.isGuestFavourite && (
                    <div className="absolute top-3 left-3 bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-900 shadow-sm z-10">
                        Guest favourite
                    </div>
                )}
                <button className="absolute top-3 right-3 p-1.5 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow border border-gray-200 z-10">
                    <Heart className="w-5 h-5 text-gray-900 stroke-2" fill="white" />
                </button>
            </div>

            <div className="p-3 space-y-2">
                <Link href={`/details/${property?.id || property?._id}`} className="">
                    <h4
                        title={getLocationString(property.location, property.title || '')}
                        className="text-sm font-semibold text-gray-900 line-clamp-1 hover:underline cursor-pointer"
                    >
                        {property.title || `Place to stay in ${getLocationString(property.location, '')}`}
                    </h4>
                </Link>

                <div className="flex items-center justify-between text-xs text-gray-900">
                    <span>
                        AED{((property.price || property?.basic_price) ?? 0).toLocaleString()} for 1 nights
                    </span>
                    {/* <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        {(property.rating ?? 4).toFixed(2)}
                    </span> */}
                </div>
            </div>
        </div>
    );
}
